import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchReferralReport, normalizeReferralCode } from '../lib/admin-referrals';
import { applicationSourceLabel } from '../lib/driver-applications';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const PAGE_SIZES = [10, 15, 25];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function formatPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  return phone || '—';
}

function statusClass(status) {
  return `admin-status admin-status--${status || 'pending'}`;
}

function buildPageList(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

function AdminReferralsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState(searchParams.get('view') || 'summary');
  const [selectedCode, setSelectedCode] = useState(searchParams.get('code') || '');
  const [summaryPage, setSummaryPage] = useState(1);
  const [detailPage, setDetailPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setSessionChecked(true);
      return undefined;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const nextUser = data.session?.user || null;
      setUser(nextUser);
      setSessionChecked(true);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user || null;
      setUser(nextUser);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError('');
    const { report: nextReport, error: loadError } = await fetchReferralReport(supabase);
    setReport(nextReport);
    setError(loadError || '');
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const q = search.trim().toLowerCase();

  const filteredSummaries = useMemo(() => {
    if (!report) return [];
    return report.referrerSummaries.filter((row) => {
      if (!q) return true;
      return (
        row.referrerName.toLowerCase().includes(q) ||
        row.referralCode.toLowerCase().includes(q) ||
        row.referrerPhone.includes(q)
      );
    });
  }, [report, q]);

  const filteredEvents = useMemo(() => {
    if (!report) return [];
    let list = report.referralEvents;
    if (selectedCode) {
      const code = normalizeReferralCode(selectedCode);
      list = list.filter((e) => e.referralCode === code);
    }
    if (!q) return list;
    return list.filter(
      (e) =>
        e.referrerName.toLowerCase().includes(q) ||
        e.referralCode.toLowerCase().includes(q) ||
        e.referred.full_name?.toLowerCase().includes(q) ||
        e.referred.phone?.includes(q)
    );
  }, [report, q, selectedCode]);

  const summaryTotalPages = Math.max(1, Math.ceil(filteredSummaries.length / pageSize));
  const safeSummaryPage = Math.min(summaryPage, summaryTotalPages);
  const summaryRows = useMemo(() => {
    const start = (safeSummaryPage - 1) * pageSize;
    return filteredSummaries.slice(start, start + pageSize);
  }, [filteredSummaries, safeSummaryPage, pageSize]);

  const detailTotalPages = Math.max(1, Math.ceil(filteredEvents.length / pageSize));
  const safeDetailPage = Math.min(detailPage, detailTotalPages);
  const detailRows = useMemo(() => {
    const start = (safeDetailPage - 1) * pageSize;
    return filteredEvents.slice(start, start + pageSize);
  }, [filteredEvents, safeDetailPage, pageSize]);

  useEffect(() => {
    setSummaryPage(1);
    setDetailPage(1);
  }, [search, view, selectedCode, pageSize]);

  const selectReferrer = (code) => {
    const normalized = normalizeReferralCode(code);
    setSelectedCode(normalized);
    setView('detail');
    setSearchParams({ view: 'detail', code: normalized || undefined });
  };

  const clearReferrerFilter = () => {
    setSelectedCode('');
    setSearchParams({ view: 'detail' });
  };

  const logout = async () => {
    await supabase?.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  if (!sessionChecked) {
    return <div className="admin-loading">Checking admin session…</div>;
  }
  if (!user) return null;

  return (
    <AdminShell
      userEmail={user.email}
      active="referrals"
      onLogout={logout}
      actions={
        <button type="button" className="btn btn--ghost-dark" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      }
    >
      <header className="admin-page-head admin-page-head--compact">
        <h1>
          Driver referrals{' '}
          <span className="admin-page-head__hint">
            (Only signups tied to a registered driver&apos;s referral code — for bonus counts)
          </span>
        </h1>
      </header>

      {error ? <div className="form-alert form-alert--error admin-error">{error}</div> : null}

      <div className="admin-referrals-kpis admin-referrals-kpis--two" aria-label="Referral totals">
        <div className="admin-referrals-kpi">
          <strong>{report?.totalReferrals ?? (loading ? '…' : 0)}</strong>
          <span>Verified referred signups</span>
        </div>
        <div className="admin-referrals-kpi">
          <strong>{report?.referrersWithReferrals ?? (loading ? '…' : 0)}</strong>
          <span>Drivers with referrals</span>
        </div>
      </div>

      <div className="admin-referrals-toolbar">
        <div className="admin-referrals-tabs" role="tablist" aria-label="Referral views">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'summary'}
            className={`admin-referrals-tab ${view === 'summary' ? 'admin-referrals-tab--active' : ''}`}
            onClick={() => {
              setView('summary');
              setSearchParams({ view: 'summary' });
            }}
          >
            By referrer (counts)
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'detail'}
            className={`admin-referrals-tab ${view === 'detail' ? 'admin-referrals-tab--active' : ''}`}
            onClick={() => {
              setView('detail');
              setSearchParams(selectedCode ? { view: 'detail', code: selectedCode } : { view: 'detail' });
            }}
          >
            Who referred whom
          </button>
        </div>
        <div className="driver-search-field admin-referrals-search">
          <span className="driver-search__label">Search</span>
          <label className="driver-search">
            <input
              className="driver-search__input"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Referrer, code, referred driver, phone…"
              autoComplete="off"
            />
          </label>
        </div>
        <label className="driver-search__page">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>per page</span>
        </label>
      </div>

      {view === 'summary' ? (
        <div className="admin-drivers__panel admin-referrals-panel">
          {loading ? (
            <p className="admin-empty">Loading referral summary…</p>
          ) : filteredSummaries.length === 0 ? (
            <p className="admin-empty">
              No drivers with verified referrals match your search. Only signups linked to a
              registered driver&apos;s code are shown here.
            </p>
          ) : (
            <>
              <div className="admin-drivers__table-wrap">
                <table className="admin-drivers-table">
                  <thead>
                    <tr>
                      <th scope="col">Referrer (driver)</th>
                      <th scope="col">Their code</th>
                      <th scope="col">Total referred</th>
                      <th scope="col">Approved</th>
                      <th scope="col">Pending</th>
                      <th scope="col">Referrer status</th>
                      <th scope="col">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((row) => (
                      <tr
                        key={row.referralCode}
                        className={`admin-table-row ${
                          selectedCode === row.referralCode ? 'admin-table-row--selected' : ''
                        }`}
                        tabIndex={0}
                        onClick={() => selectReferrer(row.referralCode)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectReferrer(row.referralCode);
                          }
                        }}
                      >
                        <td>
                          <strong>{row.referrerName}</strong>
                          <span className="admin-referrals-sub">{formatPhone(row.referrerPhone)}</span>
                        </td>
                        <td>
                          <code className="admin-referrals-code">{row.referralCode}</code>
                        </td>
                        <td>
                          <strong className="admin-referrals-count">{row.referralCount}</strong>
                        </td>
                        <td>{row.approvedCount}</td>
                        <td>{row.pendingCount}</td>
                        <td>
                          <span className={statusClass(row.referrerStatus)}>{row.referrerStatus}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="admin-referrals-link-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectReferrer(row.referralCode);
                            }}
                          >
                            View list
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ReferralsPager
                page={safeSummaryPage}
                totalPages={summaryTotalPages}
                totalItems={filteredSummaries.length}
                pageSize={pageSize}
                onPageChange={setSummaryPage}
                label="referrers"
              />
            </>
          )}
        </div>
      ) : (
        <div className="admin-drivers__panel admin-referrals-panel">
          {selectedCode ? (
            <p className="admin-referrals-filter">
              Showing referrals for code <code>{selectedCode}</code>
              <button type="button" className="admin-referrals-link-btn" onClick={clearReferrerFilter}>
                Show all
              </button>
            </p>
          ) : null}
          {loading ? (
            <p className="admin-empty">Loading referral details…</p>
          ) : filteredEvents.length === 0 ? (
            <p className="admin-empty">
              No referred signups yet
              {selectedCode ? ' for this code' : ''}.
            </p>
          ) : (
            <>
              <div className="admin-drivers__table-wrap">
                <table className="admin-drivers-table">
                  <thead>
                    <tr>
                      <th scope="col">Referrer</th>
                      <th scope="col">Code used</th>
                      <th scope="col">Referred driver</th>
                      <th scope="col">Phone</th>
                      <th scope="col">Source</th>
                      <th scope="col">Status</th>
                      <th scope="col">Signed up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((event) => (
                      <tr key={event.referred.id} className="admin-table-row">
                        <td>
                          <strong>{event.referrerName}</strong>
                        </td>
                        <td>
                          <code className="admin-referrals-code">{event.referralCode}</code>
                        </td>
                        <td>
                          <strong>{event.referred.full_name}</strong>
                        </td>
                        <td>{formatPhone(event.referred.phone)}</td>
                        <td>{applicationSourceLabel(event.referred.signup_source)}</td>
                        <td>
                          <span className={statusClass(event.referred.status)}>
                            {event.referred.status || 'pending'}
                          </span>
                        </td>
                        <td className="admin-drivers-table__date">{formatDate(event.referred.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ReferralsPager
                page={safeDetailPage}
                totalPages={detailTotalPages}
                totalItems={filteredEvents.length}
                pageSize={pageSize}
                onPageChange={setDetailPage}
                label="referrals"
              />
            </>
          )}
        </div>
      )}

      <p className="admin-referrals-footnote">
        Bonus tip: use <strong>Approved</strong> counts per referrer when paying referral bonuses.{' '}
        <Link to="/admin">Open driver applications</Link> to review pending signups.
      </p>
    </AdminShell>
  );
}

function ReferralsPager({ page, totalPages, totalItems, pageSize, onPageChange, label }) {
  const rangeStart = totalItems ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, totalItems);
  const pageList = buildPageList(page, totalPages);

  if (totalItems === 0) return null;

  return (
    <footer className="admin-drivers__footer">
      <p className="admin-drivers__summary">
        Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{' '}
        <strong>{totalItems}</strong> {label}
      </p>
      <nav className="rides-panel__pager" aria-label="Pagination">
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
        >
          First
        </button>
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Prev
        </button>
        {pageList.map((item, index) =>
          item === '…' ? (
            <span key={`gap-${index}`} className="rides-panel__ellipsis">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`rides-panel__page-btn ${item === page ? 'rides-panel__page-btn--active' : ''}`}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          )
        )}
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
        <button
          type="button"
          className="rides-panel__page-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          Last
        </button>
      </nav>
    </footer>
  );
}

export default AdminReferralsPage;
